import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useApolloClient } from '@apollo/client';
import { WishlistList } from 'packages/shared/definitions/types';

import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/tracking';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import messages from '../messages';
import { PulsatingHeart } from '../PulsatingHeart';
import { useWishlistActions } from '../useWishlistActions';
import { useWishlistLists } from '../useWishlistLists';
import { useWishlistSelectionDrawer } from '../useWishlistSelectionDrawer';

import { Wrapper, StyledAddToWishlist } from './styled';
import { updateVariantCache } from './updateVariantCache';

export const WishlistProductDetailIcon = () => {
  const { killswitch_pd_wishlist_lists } = useFeatureFlags();
  const { currentVariant, product } = useProductDetailContext();
  const { webId: productId, wishlisted } = currentVariant;

  const { minimalPriceShown, rrpShown } = usePriceLogic(currentVariant);
  const { cache } = useApolloClient();
  const listsRef = React.useRef<WishlistList[]>([]);

  const { open } = useWishlistSelectionDrawer({
    onSave: ({ addedToLists, removedFromLists }) => {
      return handleWishlistClick({
        actionLocation: 'product_detail',
        trackingPromoLabels: [
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ],
        addToLists: addedToLists,
        removeFromLists: removedFromLists,
        lists: listsRef.current,
      });
    },
    interactionSource: 'product_add',
  });

  const { fetchLists } = useWishlistLists();

  const updateCallback = React.useCallback(
    (products: Array<{ productId: string; wishlisted: boolean }>) =>
      products.forEach((p) =>
        updateVariantCache({
          wishlisted: p.wishlisted,
          productId: p.productId,
          cache,
        })
      ),
    [cache]
  );

  const { handleWishlistClick, loading } = useWishlistActions({
    productId: productId.toString(),
    onCompleted: updateCallback,
  });

  const handleClick = async () => {
    const lists = await fetchLists();
    listsRef.current = lists;
    if (lists.length === 1 || !killswitch_pd_wishlist_lists) {
      await handleWishlistClick({
        actionLocation: 'product_detail',
        trackingPromoLabels: [
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ],
        addToLists: wishlisted ? [] : [{ id: 'default' }],
        removeFromLists: wishlisted ? [{ id: 'default' }] : [],
        lists,
      });
      return;
    }

    open({
      lists,
      productId,
      productName: `${product.brand.name} ${product.collection}`,
    });
  };

  return (
    <Wrapper>
      <StyledAddToWishlist onClick={handleClick}>
        <PulsatingHeart wishlisted={wishlisted} loading={loading} />
        <FormattedMessage
          {...messages[`${wishlisted ? 'inWishlist' : 'addToWishlist'}`]}
        />
      </StyledAddToWishlist>
    </Wrapper>
  );
};
